import { 
  Box, 
  Heading, 
  Text, 
  Spinner, 
  Alert, 
  AlertIcon, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  HStack, 
  Badge, 
  Button, 
  useToast, 
  Drawer, 
  DrawerOverlay, 
  DrawerContent, 
  DrawerHeader, 
  DrawerBody, 
  DrawerFooter, 
  FormControl, 
  FormLabel, 
  Input, 
  Stack, 
  FormErrorMessage, 
  useBreakpointValue, 
  Wrap,
  WrapItem,
  useColorModeValue,
  Select
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../layouts/AdminLayout';
import { patchMatchOverride, fetchAdminMatches } from '../../services/adminMatches';
import { getPublicSettings } from '../../services/settings';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parse, format } from 'date-fns';
import { API_URL } from '../../services/api';
import TeamLogo from '../../components/common/TeamLogo';
import DateRangePicker from '../../components/common/DateRangePicker';
import { getCompetitionAliasesPublic } from '../../services/competitionAliases';

const MatchesAdminPage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const [form, setForm] = useState({
    venue_override: '',
    date_time_edit: '',
    score_override: '',
  });
  const [origDateLocal, setOrigDateLocal] = useState<string>('');

  const normalizeName = (s: string) => {
    let out = String(s || '');
    out = out
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    out = out.replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-');
    const orgPhrases = [
      'fotbalovy klub',
      'sportovni klub',
      'telovychovna jednota',
      'skolni sportovni klub',
      'fotbal',
      'futsal',
    ];
    for (const phrase of orgPhrases) {
      const re = new RegExp(`(^|\\b)${phrase}(\\b|$)`, 'g');
      out = out.replace(re, ' ');
    }
    out = out.replace(/\b(1\.)?\s*(sfc|afc|fc|fk|mfk|tj|sk|afk)\b\.?/g, ' ');
    out = out.replace(/[\.,!;:()\[\]{}]/g, ' ');
    out = out.replace(/\s+/g, ' ').trim();
    return out;
  };
  
  
  // Team name/logo editing removed

  const { data: matches = [], isLoading, error } = useQuery<any[], Error>({
    queryKey: ['admin-matches-list-cache'],
    queryFn: async () => {
      const items = await fetchAdminMatches();
      const FACR_DATE_FMT = 'dd.MM.yyyy HH:mm';
      const parseTs = (obj: any): number => {
        const s = String(obj?.date_time || (obj?.date && obj?.time ? `${obj.date} ${obj.time}` : obj?.date) || '').trim();
        if (!s) return Number.MAX_SAFE_INTEGER;
        try {
          const dt = parse(s, FACR_DATE_FMT, new Date());
          if (!isNaN(dt.getTime())) return dt.getTime();
        } catch {}
        const d2 = new Date(s);
        if (!isNaN(d2.getTime())) return d2.getTime();
        return Number.MAX_SAFE_INTEGER;
      };
      items.sort((a: any, b: any) => parseTs(a) - parseTs(b));
      return items;
    },
  });
  
  
  // Filters
  const [teamFilter, setTeamFilter] = useState('');
  const [dateFrom, setDateFrom] = useState<string>(''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState<string>(''); // YYYY-MM-DD
  const [competitionFilter, setCompetitionFilter] = useState<string>('');
  const [sideFilter, setSideFilter] = useState<'home' | 'away' | ''>('');
  const normalizedTeam = teamFilter.trim().toLowerCase();
  const FACR_DATE_FMT = 'dd.MM.yyyy HH:mm';
  const formatDisplayDate = (s: string, t?: string): string => {
    // Prefer full date-time string; if only date and time parts exist, combine them
    const str = String((s && t ? `${s} ${t}` : s) || '').trim();
    if (!str) return '';
    try {
      const dt = parse(str, FACR_DATE_FMT, new Date());
      if (!isNaN(dt.getTime())) return format(dt, FACR_DATE_FMT);
    } catch {}
    const d2 = new Date(str);
    if (!isNaN(d2.getTime())) return format(d2, FACR_DATE_FMT);
    return str;
  };
  // Club name (for side filter)
  const { data: publicSettings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: getPublicSettings,
  });
  const { data: facrClubInfo } = useQuery({
    queryKey: ['facr-club-info-name'],
    queryFn: async () => {
      const origin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
      const url = `${origin}/cache/prefetch/facr_club_info.json`;
      const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
      if (!res.ok) return null;
      return await res.json();
    },
  });
  const clubName: string = (publicSettings as any)?.club_name || (facrClubInfo as any)?.name || '';
  const normalize = (s: string) => String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
  const stripPrefixes = (s: string) => {
    let x = normalize(s);
    // Common Czech club prefixes/words
    x = x.replace(/\b(mestsky|m\.?f\.?k\.?|mfk|tj|sk|sokol|fotbalovy|fotbalový|fotbalovy\s+klub|fotbalovy\s+klub)\b/g, '').replace(/\s+/g, ' ').trim();
    return x;
  };
  const clubNorm = normalize(clubName);
  const clubStrip = stripPrefixes(clubName);
  const teamMatchesClub = (team: string): boolean => {
    const t = normalize(team);
    const ts = stripPrefixes(team);
    return !!clubNorm && (t.includes(clubNorm) || ts.includes(clubStrip) || t.endsWith(clubStrip) || clubStrip.endsWith(ts));
  };
  // Load competition aliases (ordered by display_order in backend)
  const { data: compAliases = [] } = useQuery({
    queryKey: ['competition-aliases-public'],
    queryFn: getCompetitionAliasesPublic,
  });
  const competitionOptions = useMemo(() => {
    const set = new Set<string>();
    const competitionMap = new Map<string, { name: string; alias?: string; order: number }>();
    
    // Collect unique competitions and find their aliases
    for (const m of matches) {
      if (m.competitionName) {
        const name = String(m.competitionName);
        if (!set.has(name)) {
          set.add(name);
          
          // Find matching alias
          let foundAlias: any = null;
          let order = Number.MAX_SAFE_INTEGER;
          const n = normalizeName(name);
          
          if (Array.isArray(compAliases) && compAliases.length > 0) {
            for (let i = 0; i < compAliases.length; i++) {
              const al: any = compAliases[i] as any;
              const a1 = normalizeName(String(al.alias || ''));
              const a2 = normalizeName(String(al.original_name || ''));
              
              if ((a1 && (n.includes(a1) || a1.includes(n))) || (a2 && (n.includes(a2) || a2.includes(n)))) {
                foundAlias = al;
                order = i;
                break;
              }
            }
          }
          
          competitionMap.set(name, {
            name,
            alias: foundAlias ? String(foundAlias.alias || '') : undefined,
            order
          });
        }
      }
    }
    
    // Convert to array and sort by alias order, then by name
    const arr = Array.from(competitionMap.values());
    return arr.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name);
    });
  }, [matches, compAliases]);
  const filteredMatches = matches.filter((m: any) => {
    // team filter
    const teamOk = normalizedTeam
      ? (
          sideFilter === 'home'
            ? [m.home, m.home_team].filter(Boolean).some((v: string) => String(v).toLowerCase().includes(normalizedTeam))
            : sideFilter === 'away'
            ? [m.away, m.away_team].filter(Boolean).some((v: string) => String(v).toLowerCase().includes(normalizedTeam))
            : [m.home, m.home_team, m.away, m.away_team]
                .filter(Boolean)
                .some((v: string) => String(v).toLowerCase().includes(normalizedTeam))
        )
      : true;
    if (!teamOk) return false;

    // competition filter
    if (competitionFilter && String(m.competitionName || '') !== competitionFilter) return false;

    // side filter based on club name
    if (sideFilter && clubNorm) {
      const homeName = String(m.home || m.home_team || '');
      const awayName = String(m.away || m.away_team || '');
      if (sideFilter === 'home' && !teamMatchesClub(homeName)) return false;
      if (sideFilter === 'away' && !teamMatchesClub(awayName)) return false;
    }

    // date parse
    const dtStr = String(m.date_time || (m.date && m.time ? `${m.date} ${m.time}` : m.date) || '');
    let ts = NaN;
    try {
      ts = parse(dtStr, FACR_DATE_FMT, new Date()).getTime();
    } catch (_) {
      const d2 = new Date(dtStr);
      ts = d2.getTime();
    }
    if (isNaN(ts)) return true; // if can't parse, let it pass other filters

    // date range filter
    if (dateFrom) {
      const fromTs = new Date(dateFrom + 'T00:00:00').getTime();
      if (!isNaN(fromTs) && ts < fromTs) return false;
    }
    if (dateTo) {
      const toTs = new Date(dateTo + 'T23:59:59').getTime();
      if (!isNaN(toTs) && ts > toTs) return false;
    }
    return true;
  });

  // Pagination (Load more) + page size selector
  const [pageSize, setPageSize] = useState(50);
  const [limit, setLimit] = useState(50);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters from URL on first load and when data changes (so comps are known)
  useEffect(() => {
    const spTeam = searchParams.get('team') || '';
    const spFrom = searchParams.get('from') || '';
    const spTo = searchParams.get('to') || '';
    const spComp = searchParams.get('comp') || '';
    const spVenue = searchParams.get('venue') || '';
    const spSide = searchParams.get('side') || '';
    const spSize = parseInt(searchParams.get('size') || '') || undefined;
    const spLimit = parseInt(searchParams.get('limit') || '') || undefined;
    if (spTeam) setTeamFilter(spTeam);
    if (spFrom) setDateFrom(spFrom);
    if (spTo) setDateTo(spTo);
    if (spComp) setCompetitionFilter(spComp);
    // venue filter removed
    if (spSide === 'home' || spSide === 'away') setSideFilter(spSide);
    if (spSize) {
      setPageSize(spSize);
      setLimit(spSize);
    }
    if (spLimit) setLimit(spLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync when filters/pagination change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (teamFilter) params.team = teamFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (competitionFilter) params.comp = competitionFilter;
    // venue filter removed
    if (sideFilter) params.side = sideFilter;
    if (pageSize !== 50) params.size = String(pageSize);
    if (limit !== pageSize) params.limit = String(limit);
    setSearchParams(params, { replace: true });
  }, [teamFilter, dateFrom, dateTo, competitionFilter, sideFilter, pageSize, limit, setSearchParams]);
  useEffect(() => {
    // reset pagination on filter change
    setLimit(pageSize);
  }, [normalizedTeam, dateFrom, dateTo, competitionFilter, sideFilter, clubNorm, pageSize]);
  const visibleMatches = filteredMatches.slice(0, limit);

  // Date presets
  const setThisWeek = () => {
    const now = new Date();
    const day = now.getDay(); // 0 Sun .. 6 Sat
    const diffToMonday = (day === 0 ? -6 : 1 - day); // Monday start
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const f = monday.toISOString().slice(0, 10);
    const t = sunday.toISOString().slice(0, 10);
    setDateFrom(f);
    setDateTo(t);
  };
  const setNext30Days = () => {
    const now = new Date();
    const to = new Date(now);
    to.setDate(now.getDate() + 30);
    const f = now.toISOString().slice(0, 10);
    const t = to.toISOString().slice(0, 10);
    setDateFrom(f);
    setDateTo(t);
  };

  // Export CSV of filtered results
  const exportCsv = () => {
    const rows = filteredMatches.map((m: any) => {
      const date = m.date_time || m.date || '';
      const comp = m.competitionName || '';
      const home = m.home || m.home_team || '';
      const away = m.away || m.away_team || '';
      const score = m.score || (m.result_home != null && m.result_away != null ? `${m.result_home}:${m.result_away}` : '');
      const venue = m.venue || '';
      return { date, competition: comp, home, away, score, venue };
    });
    const headers = ['date', 'competition', 'home', 'away', 'score', 'venue'];
    const escape = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape((r as any)[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'matches.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Datetime validation for datetime-local
  const isDateInvalid = form.date_time_edit.trim() !== '' && isNaN(new Date(form.date_time_edit).getTime());

  const saveMutation = useMutation({
    mutationFn: async () => {
      const externalMatchId: string = String((selected?.match_id ?? selected?.id ?? '')).trim();
      if (!externalMatchId) throw new Error('Chybí match_id');
      const body: any = {};
      // Venue: send only if changed
      const currentVenue = String(selected?.venue || '');
      if (form.venue_override.trim() !== currentVenue) {
        body.venue_override = form.venue_override.trim() === '' ? null : form.venue_override.trim();
      }
      // Score: send only if changed
      const currentScore = String(selected?.score || (selected?.result_home != null && selected?.result_away != null ? `${selected.result_home}:${selected.result_away}` : '') || '');
      if (form.score_override.trim() !== currentScore) {
        body.score_override = form.score_override.trim() === '' ? null : form.score_override.trim();
      }
      // Datetime: send only if changed
      if (form.date_time_edit !== origDateLocal) {
        body.date_time_override = form.date_time_edit.trim() === '' ? null : form.date_time_edit;
      }
      // If nothing changed, do nothing
      if (Object.keys(body).length === 0) return { ok: true };
      await patchMatchOverride(externalMatchId, body);
      return { ok: true };
    },
    onSuccess: () => {
      toast({ title: 'Uloženo', status: 'success' });
      setIsOpen(false);
      setSelected(null);
      queryClient.invalidateQueries({ queryKey: ['admin-matches-list-cache'] });
    },
    onError: (e: any) => {
      toast({ title: 'Uložení selhalo', description: e?.message || 'Zkuste to znovu', status: 'error' });
    },
  });

  const openEdit = (m: any) => {
    setSelected(m);
    const facrStr: string = m.date_time || (m.date && m.time ? `${m.date} ${m.time}` : m.date) || '';
    let localStr = '';
    if (facrStr) {
      try {
        const dt = parse(String(facrStr), 'dd.MM.yyyy HH:mm', new Date());
        if (!isNaN(dt.getTime())) {
          const pad = (n: number) => String(n).padStart(2, '0');
          localStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        }
      } catch (_) {
        const d2 = new Date(facrStr);
        if (!isNaN(d2.getTime())) {
          const pad = (n: number) => String(n).padStart(2, '0');
          localStr = `${d2.getFullYear()}-${pad(d2.getMonth() + 1)}-${pad(d2.getDate())}T${pad(d2.getHours())}:${pad(d2.getMinutes())}`;
        }
      }
    }
    setOrigDateLocal(localStr);
    setForm({
      venue_override: m.venue || '',
      date_time_edit: localStr,
      score_override: String(m.score || (m.result_home != null && m.result_away != null ? `${m.result_home}:${m.result_away}` : '') || ''),
    });
    setIsOpen(true);
  };

  // Removed autofocus logic for team inputs

  const drawerSize = useBreakpointValue({ base: 'full', md: 'md' });
  // Horizontal scroll affordance
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  
  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);
  const animationRef = useRef<number | null>(null);
  const scrollRaf = useRef<number | null>(null);
  
  const updateScrollShadow = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    if (left !== canScrollLeft) setCanScrollLeft(left);
    if (right !== canScrollRight) setCanScrollRight(right);
  };
  
  // Drag-to-scroll handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    // Cancel any ongoing momentum animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    lastXRef.current = e.pageX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    scrollRef.current.style.cursor = 'grabbing';
    scrollRef.current.style.userSelect = 'none';
    scrollRef.current.style.scrollBehavior = 'auto'; // Disable smooth scroll during drag
  };
  
  const handleMouseLeave = () => {
    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
      scrollRef.current.style.scrollBehavior = 'smooth';
      
      // Apply momentum scrolling
      const velocity = velocityRef.current;
      if (Math.abs(velocity) > 0.5) {
        const applyMomentum = () => {
          if (!scrollRef.current) return;
          velocityRef.current *= 0.95; // Deceleration factor
          scrollRef.current.scrollLeft -= velocityRef.current;
          
          if (Math.abs(velocityRef.current) > 0.5) {
            animationRef.current = requestAnimationFrame(applyMomentum);
          } else {
            animationRef.current = null;
          }
        };
        animationRef.current = requestAnimationFrame(applyMomentum);
      }
    }
  };
  
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier (reduced for smoother feel)
    scrollRef.current.scrollLeft = scrollLeft - walk;
    
    // Calculate velocity for momentum
    const now = Date.now();
    const timeDelta = now - lastTimeRef.current;
    if (timeDelta > 0) {
      const currentX = e.pageX;
      const distance = currentX - lastXRef.current;
      velocityRef.current = distance / timeDelta * 16; // Normalize to ~60fps
      lastXRef.current = currentX;
      lastTimeRef.current = now;
    }
  };
  
  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    const touch = e.touches[0];
    setIsDragging(true);
    setStartX(touch.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
    lastXRef.current = touch.pageX;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    if (scrollRef.current) scrollRef.current.style.scrollBehavior = 'auto';
  };
  
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;
    const touch = e.touches[0];
    const x = touch.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
    
    const now = Date.now();
    const timeDelta = now - lastTimeRef.current;
    if (timeDelta > 0) {
      const currentX = touch.pageX;
      const distance = currentX - lastXRef.current;
      velocityRef.current = distance / timeDelta * 16;
      lastXRef.current = currentX;
      lastTimeRef.current = now;
    }
  };
  
  const handleTouchEnd = () => {
    setIsDragging(false);
    if (scrollRef.current) {
      scrollRef.current.style.scrollBehavior = 'smooth';
      
      const velocity = velocityRef.current;
      if (Math.abs(velocity) > 0.5) {
        const applyMomentum = () => {
          if (!scrollRef.current) return;
          velocityRef.current *= 0.95;
          scrollRef.current.scrollLeft -= velocityRef.current;
          
          if (Math.abs(velocityRef.current) > 0.5) {
            animationRef.current = requestAnimationFrame(applyMomentum);
          } else {
            animationRef.current = null;
          }
        };
        animationRef.current = requestAnimationFrame(applyMomentum);
      }
    }
  };
  
  // Utility to check if match is in the past
  const isMatchPast = (dateTimeStr: string): boolean => {
    if (!dateTimeStr) return false;
    // Try full date+time first: dd.MM.yyyy HH:mm
    try {
      const dt = parse(dateTimeStr, FACR_DATE_FMT, new Date());
      if (!isNaN(dt.getTime())) return dt.getTime() < Date.now();
    } catch {}
    // If only date is present: dd.MM.yyyy
    try {
      const dOnly = parse(dateTimeStr, 'dd.MM.yyyy', new Date());
      if (!isNaN(dOnly.getTime())) return dOnly.getTime() < Date.now();
    } catch {}
    // Fallback to Date constructor (RFC3339 etc.)
    const d2 = new Date(dateTimeStr);
    if (!isNaN(d2.getTime())) return d2.getTime() < Date.now();
    return false;
  };

  useEffect(() => {
    updateScrollShadow();
    const onResize = () => updateScrollShadow();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      // Cleanup momentum animation on unmount
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const headerBg = useColorModeValue('brand.primary', 'gray.700');
  const headerText = useColorModeValue('text.onPrimary', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const edgeGradientLeft = useColorModeValue('linear(to-r, white, transparent)', 'linear(to-r, gray.800, transparent)');
  const edgeGradientRight = useColorModeValue('linear(to-l, white, transparent)', 'linear(to-l, gray.800, transparent)');
  const pastMatchBg = useColorModeValue('gray.100', 'gray.700');
  const futureMatchBg = useColorModeValue('white', 'gray.800');
  const pastMatchHoverBg = useColorModeValue('gray.200', 'gray.600');
  const futureMatchHoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <Box mb={6}>
          <Heading size="lg" mb={2}>Správa zápasů</Heading>
          <Text color={useColorModeValue('gray.600', 'gray.400')}>
            Správa a úprava zápasů. Můžete upravovat informace o zápasech, včetně názvů týmů, termínů, log a dalších detailů.
          </Text>
        </Box>

        {isLoading ? (
          <HStack spacing={3} mb={4}>
            <Spinner />
            <Text>Načítám zápasy…</Text>
          </HStack>
        ) : error ? (
          <Alert status="error" variant="left-accent" mb={4}>
            <AlertIcon />
            Nepodařilo se načíst zápasy.
          </Alert>
        ) : (
          <Box>
            <Wrap mb={4} spacing={3} align="center">
              <WrapItem minW="160px">
                <Select size="sm" value={sideFilter} onChange={(e) => setSideFilter((e.target.value as any) || '')}>
                  <option value="">Všechny strany</option>
                  <option value="home">Domácí</option>
                  <option value="away">Hosté</option>
                </Select>
              </WrapItem>
              <WrapItem flex={1} minW="220px">
                <Input
                  placeholder="Filtrovat podle týmu…"
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  size="sm"
                />
              </WrapItem>
              <WrapItem>
                <DateRangePicker
                  from={dateFrom || undefined}
                  to={dateTo || undefined}
                  onChange={(fromValue, toValue) => {
                    setDateFrom(fromValue);
                    setDateTo(toValue);
                  }}
                  size="sm"
                />
              </WrapItem>
              <WrapItem minW="220px">
                <Select size="sm" value={competitionFilter} onChange={(e) => setCompetitionFilter(e.target.value)}>
                  <option value="">Všechny soutěže</option>
                  {competitionOptions.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.alias ? `${c.alias} - ${c.name}` : c.name}
                    </option>
                  ))}
                </Select>
              </WrapItem>
              {(teamFilter || dateFrom || dateTo || competitionFilter || sideFilter) && (
                <WrapItem>
                  <Button size="sm" variant="outline" colorScheme="red" onClick={() => { setTeamFilter(''); setDateFrom(''); setDateTo(''); setCompetitionFilter(''); setSideFilter(''); }} borderRadius="md">
                    Vymazat filtry
                  </Button>
                </WrapItem>
              )}
              <WrapItem>
                <HStack>
                  <Text fontSize="sm">Na stránku:</Text>
                  <Select size="sm" value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value) || 25)} width="auto">
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </Select>
                </HStack>
              </WrapItem>
              <WrapItem>
                <Button size="sm" onClick={exportCsv} bg="brand.primary" color="text.onPrimary" _hover={{ filter: 'brightness(0.95)' }} borderRadius="md">Export CSV</Button>
              </WrapItem>
              <WrapItem>
                <Text color="gray.500" fontSize="sm">
                  Zobrazeno {visibleMatches.length} / {filteredMatches.length}
                </Text>
              </WrapItem>
            </Wrap>
            {showScrollHint && (
              <Text fontSize="xs" color="blue.600" fontWeight="600" mb={2} display="flex" alignItems="center" gap={1}>
                💡 Tip: Tabulku můžete plynule posouvat tažením myší nebo prstem →
              </Text>
            )}
            <Box
              ref={scrollRef}
              overflowX="auto"
              borderWidth="2px"
              borderRadius="xl"
              borderColor={borderColor}
              w="full"
              bg={cardBg}
              boxShadow="md"
              maxW="100%"
              position="relative"
              cursor="grab"
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onScroll={(e) => {
                if (scrollRaf.current == null) {
                  scrollRaf.current = requestAnimationFrame(() => {
                    const el = scrollRef.current;
                    if (el) {
                      updateScrollShadow();
                      if (el.scrollLeft > 0 && showScrollHint) setShowScrollHint(false);
                    }
                    scrollRaf.current = null;
                  });
                }
              }}
              sx={{
                WebkitOverflowScrolling: 'touch',
                scrollBehavior: 'smooth',
                transform: 'translateZ(0)',
                willChange: 'transform',
                overscrollBehaviorX: 'contain',
                touchAction: 'pan-x',
                'th, td': { whiteSpace: 'nowrap' },
                '::-webkit-scrollbar': { height: '14px' },
                '::-webkit-scrollbar-thumb': { 
                  background: '#3182ce', 
                  borderRadius: '10px',
                  border: '3px solid transparent',
                  backgroundClip: 'content-box',
                  transition: 'background 0.2s ease',
                  '&:hover': { background: '#2c5aa0', backgroundClip: 'content-box' },
                  '&:active': { background: '#2a4e8a', backgroundClip: 'content-box' }
                },
                '::-webkit-scrollbar-track': { 
                  background: useColorModeValue('#f7fafc', '#2d3748'),
                  borderRadius: '10px',
                  margin: '0 8px',
                  border: '1px solid',
                  borderColor: useColorModeValue('#e2e8f0', '#4a5568')
                },
              }}
            >
              {/* Gradient edges to indicate horizontal scroll */}
              {canScrollLeft && (
                <Box position="sticky" left={0} top={0} bottom={0} w="24px" pointerEvents="none"
                  bgGradient={edgeGradientLeft}
                  zIndex={1}
                />
              )}
              {canScrollRight && (
                <Box position="sticky" right={0} top={0} bottom={0} w="24px" pointerEvents="none"
                  bgGradient={edgeGradientRight}
                  zIndex={1}
                />
              )}
              <Table size="sm" sx={{ width: 'max-content' }}>
                <Thead sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: headerBg, 'th': { bg: headerBg, color: headerText, fontWeight: 'bold', textTransform: 'uppercase', fontSize: 'xs', letterSpacing: '0.05em' } }}>
                  <Tr>
                    <Th minW="140px">Datum</Th>
                    <Th minW="200px">Soutěž</Th>
                    <Th minW="260px">Domácí</Th>
                    <Th minW="80px" textAlign="center">Skóre</Th>
                    <Th minW="260px">Hosté</Th>
                    <Th minW="220px">Místo</Th>
                    <Th minW="180px">Akce</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredMatches.length === 0 ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Text color="gray.500">Žádné zápasy k zobrazení.</Text>
                      </Td>
                    </Tr>
                  ) : (
                    visibleMatches.map((m: any, idx: number) => {
                      const isPast = isMatchPast(m.date_time || (m.date && m.time ? `${m.date} ${m.time}` : m.date) || '');
                      const hasScore = m.score || (m.result_home != null && m.result_away != null);
                      return (
                        <Tr 
                          key={m.id ?? idx} 
                          bg={isPast ? pastMatchBg : futureMatchBg}
                          _hover={{ bg: isPast ? pastMatchHoverBg : futureMatchHoverBg }}
                          opacity={isPast ? 0.85 : 1}
                          transition="all 0.2s"
                        >
                          <Td>
                            <HStack spacing={2}>
                              <Text>{formatDisplayDate(String(m.date || m.date_time || ''), String(m.time || ''))}</Text>
                              {isPast && <Badge colorScheme="gray" fontSize="xs">Odehráno</Badge>}
                              {!isPast && <Badge colorScheme="green" fontSize="xs">Nadcházející</Badge>}
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Badge bg="brand.primary" color="text.onPrimary" borderRadius="md">{m.competitionName}</Badge>
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <TeamLogo
                                teamId={m.home_id ? String(m.home_id) : undefined}
                                teamName={m.home || m.home_team || ''}
                                facrLogo={m.home_logo_url}
                                size="small"
                                alt={m.home || m.home_team || ''}
                                loading="lazy"
                                decoding="async"
                                draggable={false}
                              />
                              <Text fontWeight={isPast ? 'normal' : 'medium'}>{m.home || m.home_team || ''}</Text>
                              <Button size="xs" variant="outline" onClick={() => openEdit(m)} borderRadius="md" _hover={{ borderColor: 'brand.primary', color: 'brand.primary' }}>Tým</Button>
                            </HStack>
                          </Td>
                          <Td textAlign="center">
                            <Text fontWeight={hasScore ? 'bold' : 'normal'} color={hasScore ? 'blue.600' : 'gray.500'}>
                              {m.score || (m.result_home != null && m.result_away != null ? `${m.result_home}:${m.result_away}` : '–:–')}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <TeamLogo
                                teamId={m.away_id ? String(m.away_id) : undefined}
                                teamName={m.away || m.away_team || ''}
                                facrLogo={m.away_logo_url}
                                size="small"
                                alt={m.away || m.away_team || ''}
                                loading="lazy"
                                decoding="async"
                                draggable={false}
                              />
                              <Text fontWeight={isPast ? 'normal' : 'medium'}>{m.away || m.away_team || ''}</Text>
                              <Button size="xs" variant="outline" onClick={() => openEdit(m)} borderRadius="md" _hover={{ borderColor: 'brand.primary', color: 'brand.primary' }}>Tým</Button>
                            </HStack>
                          </Td>
                          <Td>{m.venue || ''}</Td>
                          <Td>
                            <HStack spacing={2}>
                              <Button size="xs" onClick={() => openEdit(m)} bg="brand.primary" color="text.onPrimary" _hover={{ filter: 'brightness(0.95)' }} borderRadius="md">Upravit</Button>
                            </HStack>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </Box>
            {filteredMatches.length > visibleMatches.length && (
              <HStack justify="center" mt={6}>
                <Button onClick={() => setLimit((n) => n + pageSize)} size="lg" bg="brand.primary" color="text.onPrimary" _hover={{ filter: 'brightness(0.95)' }} borderRadius="lg" px={8}>
                  Načíst další ({filteredMatches.length - visibleMatches.length} zápasů)
                </Button>
              </HStack>
            )}
          </Box>
        )}
      </Box>

      {/* Edit Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={() => setIsOpen(false)} size={drawerSize}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Upravit zápas</DrawerHeader>
          <DrawerBody>
            {!selected ? (
              <Text color="gray.500">Není vybrán žádný zápas.</Text>
            ) : (
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel>Datum a čas</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.date_time_edit}
                    onChange={(e) => setForm((f) => ({ ...f, date_time_edit: e.target.value }))}
                  />
                  {isDateInvalid && (
                    <FormErrorMessage>Neplatný formát data/času</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>Místo</FormLabel>
                  <Input
                    placeholder="Místo konání"
                    value={form.venue_override}
                    onChange={(e) => setForm((f) => ({ ...f, venue_override: e.target.value }))}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Skóre</FormLabel>
                  <Input
                    placeholder="např. 2:1"
                    value={form.score_override}
                    onChange={(e) => setForm((f) => ({ ...f, score_override: e.target.value }))}
                  />
                </FormControl>

                {/* Team name/logo editing removed */}

                
              </Stack>
            )}
          </DrawerBody>
          <DrawerFooter>
            <HStack spacing={3}>
              <Button variant="outline" onClick={() => setIsOpen(false)}>Zavřít</Button>
              <Button colorScheme="blue" isLoading={saveMutation.isPending} onClick={() => saveMutation.mutate()} isDisabled={isDateInvalid}>
                Uložit změny
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
 
    </AdminLayout>
  );
};

export default MatchesAdminPage;
